/**
 * 4-up grid of template/option cards, single-select. The whole card is the
 * control — no per-card "Select" button.
 * Spec: assets/handoffs/design_handoff_new_calendar_page/README.md §2
 *
 * Roving tabindex, same pattern as Tabs: only the selected card sits in the
 * tab order; arrow keys move the roving focus across the grid (Left/Right by
 * one, Up/Down by a full row), Home/End jump to first/last. Space/Enter
 * (native button activation) selects.
 *
 * Keyboard focus and selection are drawn separately on purpose (README §2):
 * selection is an inset border+ring+tick, focus is an outside offset
 * outline, so a focused-but-unselected card never reads as selected.
 *
 * @param {Array<{key:string, name:string, pitch:string, tag:string, thumbnailSrc:string}>} items
 * @param {string} value - selected item's key
 * @param {function} onChange - (key) => void
 * @param {number=4} columns - cards per row, for roving Up/Down math; CSS grid columns are controlled by className/media queries, this only affects keyboard nav
 */
import React, { useRef } from "react";
import classNames from "classnames";
import Icon from "../icons/Icon";
import styles from "./RadioCardGrid.module.scss";

const RadioCardGrid = ({ items, value, onChange, columns = 4, className, ...rest }) => {
  const refs = useRef([]);

  const focusCard = (index) => refs.current[index]?.focus();

  const select = (index) => {
    onChange?.(items[index].key);
    focusCard(index);
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "ArrowRight") {
      e.preventDefault();
      select((index + 1) % items.length);
    } else if (e.key === "ArrowLeft") {
      e.preventDefault();
      select((index - 1 + items.length) % items.length);
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      select((index + columns) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      select((index - columns + items.length) % items.length);
    } else if (e.key === "Home") {
      e.preventDefault();
      select(0);
    } else if (e.key === "End") {
      e.preventDefault();
      select(items.length - 1);
    }
  };

  return (
    <div role="radiogroup" className={classNames(styles.root, className)} {...rest}>
      {items.map((item, index) => {
        const selected = item.key === value;
        return (
          <button
            key={item.key}
            ref={(el) => (refs.current[index] = el)}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={selected || (!value && index === 0) ? 0 : -1}
            className={classNames(styles.card, { [styles["is-selected"]]: selected })}
            onClick={() => select(index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
          >
            <span className={styles.thumb}>
              <img src={item.thumbnailSrc} alt="" className={styles.thumbImg} />
            </span>
            <span className={styles.body}>
              <span className={styles.name}>{item.name}</span>
              <span className={styles.pitch}>{item.pitch}</span>
              {item.tag && <span className={styles.tag}>{item.tag}</span>}
            </span>
            {selected && (
              <span className={styles.tick} aria-hidden="true">
                <Icon name="Check" size={12} className={styles.tickIcon} />
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default RadioCardGrid;
